/**
 * Stars Layer Strategy Implementation
 *
 * Implements layer-specific logic for Stars layer processing,
 * validation, and metadata management.
 */

import {
  STAR_METADATA,
  validateStringMaxLength,
  validateArrayLength,
  validateArrayItems,
} from '../../../constants/apiMetadataConstraints';
import {
  BaseLayerStrategy,
  LayerFieldConfig,
  ValidationResult,
} from './LayerStrategy';
import { StarsLayerMetadata } from '../../../types/layerMetadata';

export class StarsLayerStrategy extends BaseLayerStrategy {
  readonly layerCode = 'S';
  readonly layerName = 'Stars';
  readonly layerDescription =
    'Star performers with unique identities, styles, and personalities';

  getRequiredFields(): string[] {
    // Match StarMetadataRequest (OpenAPI): starName, gender, ageGroup, ethnicity, mood
    return ['starName', 'gender', 'ageGroup', 'ethnicity', 'mood'];
  }

  getOptionalFields(): string[] {
    return [
      'bodyType',
      'height',
      'hairColor',
      'hairLength',
      'hairStyle',
      'hairTexture',
      'hairTreatment',
      'hairAccessories',
      'eyeColor',
      'skinTone',
      'makeupType',
      'eyeMakeup',
      'lipMakeup',
      'faceMakeup',
      'makeupEffects',
      'colorPalette',
      'archetype',
      'energy',
      'vibe',
      'careerStage',
      'performanceContext',
      'personalityTraits',
      'richDescription',
      'comprehensiveTags',
    ];
  }

  getFieldConfig(): LayerFieldConfig {
    return {
      required: this.getRequiredFields(),
      optional: this.getOptionalFields(),
      computed: ['processingMethod', 'confidence', 'tags'],
      inherited: ['ageGroup', 'ethnicity', 'archetype', 'energy', 'vibe'],
    };
  }

  extractMetadataFromAI(aiResponse: any): StarsLayerMetadata {
    const layerMetadata =
      aiResponse.data?.layerMetadata ||
      aiResponse.layerMetadata ||
      aiResponse.starsMetadata ||
      {};

    return {
      layerType: 'stars',

      // Core identity
      starName: layerMetadata.starName || aiResponse.starName || '',
      gender: layerMetadata.gender || aiResponse.gender || 'Male',
      ageGroup: layerMetadata.ageGroup || aiResponse.ageGroup || 'adult',
      ethnicity: layerMetadata.ethnicity || aiResponse.ethnicity || '',
      mood: this.normalizeArray(layerMetadata.mood || aiResponse.mood),

      // Physical traits
      bodyType: layerMetadata.bodyType || aiResponse.bodyType || 'Average',
      height: layerMetadata.height || aiResponse.height || 'Average',
      hairColor: layerMetadata.hairColor || aiResponse.hairColor || '',
      hairLength: layerMetadata.hairLength || aiResponse.hairLength || 'Medium',
      hairStyle: layerMetadata.hairStyle || aiResponse.hairStyle || 'Straight',
      hairTexture:
        layerMetadata.hairTexture || aiResponse.hairTexture || 'Medium',
      hairTreatment:
        layerMetadata.hairTreatment || aiResponse.hairTreatment || 'Natural',
      hairAccessories:
        layerMetadata.hairAccessories || aiResponse.hairAccessories || 'None',
      eyeColor: layerMetadata.eyeColor || aiResponse.eyeColor || '',
      skinTone: layerMetadata.skinTone || aiResponse.skinTone || 'Medium',

      // Makeup & style
      makeupType:
        layerMetadata.makeupType || aiResponse.makeupType || 'Natural',
      eyeMakeup: layerMetadata.eyeMakeup || aiResponse.eyeMakeup || 'Natural',
      lipMakeup: layerMetadata.lipMakeup || aiResponse.lipMakeup || 'Natural',
      faceMakeup:
        layerMetadata.faceMakeup || aiResponse.faceMakeup || 'Light Coverage',
      makeupEffects:
        layerMetadata.makeupEffects || aiResponse.makeupEffects || 'None',
      colorPalette:
        layerMetadata.colorPalette || aiResponse.colorPalette || 'Neutral',

      // Performance & personality
      archetype: layerMetadata.archetype || aiResponse.archetype || 'emerging',
      energy: layerMetadata.energy || aiResponse.energy || 'Medium',
      vibe: layerMetadata.vibe || aiResponse.vibe || 'Calm',
      careerStage:
        layerMetadata.careerStage || aiResponse.careerStage || 'Rising',
      performanceContext: this.normalizeArray(
        layerMetadata.performanceContext || aiResponse.performanceContext
      ),
      targetAudience: this.normalizeArray(
        layerMetadata.targetAudience || aiResponse.targetAudience
      ),
      personalityTraits: this.normalizeArray(
        layerMetadata.personalityTraits || aiResponse.personalityTraits
      ),

      // Rich content
      richDescription:
        layerMetadata.richDescription || aiResponse.richDescription || '',
      comprehensiveTags: this.normalizeArray(
        layerMetadata.comprehensiveTags || aiResponse.comprehensiveTags
      ),

      // Asset relationships
      variantName: aiResponse.variantName || '',
      variantType: aiResponse.variantType || '',
      baseAssetId: aiResponse.baseAssetId || '',
    };
  }

  enhanceWithDefaults(metadata: StarsLayerMetadata): StarsLayerMetadata {
    const defaults = this.getDefaultMetadata();

    return {
      ...defaults,
      ...metadata,
      layerType: 'stars',

      // Apply intelligent defaults
      starName: metadata.starName || 'Unknown Star',
      gender: metadata.gender || 'Male',
      ageGroup: metadata.ageGroup || 'adult',
      ethnicity: metadata.ethnicity || '',
      bodyType: metadata.bodyType || 'Average',
      height: metadata.height || 'Average',
      makeupType: metadata.makeupType || 'Natural',
      energy: metadata.energy || 'Medium',
      vibe: metadata.vibe || 'Calm',
      archetype: metadata.archetype || 'emerging',

      // Ensure arrays are properly initialized
      performanceContext: this.normalizeArray(metadata.performanceContext),
      personalityTraits: this.normalizeArray(metadata.personalityTraits),
      comprehensiveTags: this.normalizeArray(metadata.comprehensiveTags),
    };
  }

  generateTags(metadata: StarsLayerMetadata, description?: string): string[] {
    const tags = new Set<string>();

    // Basic layer tags
    tags.add('stars');
    tags.add('talent');
    tags.add('performance');

    // Gender-based tags
    if (metadata.gender) {
      tags.add(`${metadata.gender.toLowerCase()}-performer`);
      if (metadata.gender !== 'unknown') {
        tags.add(`${metadata.gender.toLowerCase()}-star`);
      }
    }

    // Age-based tags
    if (metadata.ageGroup) {
      tags.add(`${metadata.ageGroup.toLowerCase()}-performer`);
      tags.add(`${metadata.ageGroup.toLowerCase()}-talent`);
    }

    // Physical appearance tags
    if (metadata.hairColor) {
      tags.add(`${metadata.hairColor.toLowerCase()}-hair`);
      tags.add(`${metadata.hairColor.toLowerCase()}-hair-star`);
    }

    if (metadata.eyeColor) {
      tags.add(`${metadata.eyeColor.toLowerCase()}-eyes`);
    }

    // Energy and vibe tags
    if (metadata.energy) {
      tags.add(`${metadata.energy.toLowerCase()}-energy`);
      tags.add(`${metadata.energy.toLowerCase()}-performer`);
    }

    if (metadata.vibe) {
      tags.add(`${metadata.vibe.toLowerCase()}-vibe`);
      tags.add(`${metadata.vibe.toLowerCase()}-personality`);
    }

    // Personality traits tags
    if (metadata.personalityTraits && metadata.personalityTraits.length > 0) {
      metadata.personalityTraits.forEach((trait: string) => {
        tags.add(`${trait.toLowerCase()}-personality`);
        tags.add(`${trait.toLowerCase()}-character`);
      });
    }

    // Archetype tags
    if (metadata.archetype) {
      tags.add(`${metadata.archetype.toLowerCase()}-archetype`);
      tags.add(`${metadata.archetype.toLowerCase()}-type`);
    }

    // Comprehensive descriptive tags
    tags.add('versatile');
    tags.add('contemporary');
    tags.add('expressive');
    tags.add('charismatic');
    tags.add('dynamic');
    tags.add('engaging');
    tags.add('authentic');
    tags.add('memorable');

    return Array.from(tags).slice(0, 30); // Limit to 30 tags
  }

  categorizeTags(tags: string[]): {
    primary: string[];
    secondary: string[];
    descriptive: string[];
  } {
    const categories = {
      primary: [] as string[],
      secondary: [] as string[],
      descriptive: [] as string[],
    };

    tags.forEach(tag => {
      const lowerTag = tag.toLowerCase();

      // Primary tags - core identifying features
      if (
        lowerTag.includes('hair') ||
        lowerTag.includes('eye') ||
        lowerTag.includes('skin')
      ) {
        categories.primary.push(tag);
      }
      // Secondary tags - performance characteristics
      else if (
        lowerTag.includes('energy') ||
        lowerTag.includes('vibe') ||
        lowerTag.includes('performer') ||
        lowerTag.includes('style')
      ) {
        categories.secondary.push(tag);
      }
      // Descriptive tags - general attributes
      else {
        categories.descriptive.push(tag);
      }
    });

    return categories;
  }

  validateBusinessRules(metadata: StarsLayerMetadata): ValidationResult {
    const errors: any[] = [];
    const warnings: any[] = [];

    // OpenAPI StarMetadataRequest: string maxLength 255, arrays max 10
    if (metadata.starName) {
      const msg = validateStringMaxLength(metadata.starName, STAR_METADATA.stringMaxLength, 'Star name');
      if (msg) errors.push({ field: 'starName', message: msg, severity: 'error' });
    }
    for (const [key, opts] of [
      ['musicalStyle', STAR_METADATA.musicalStyle],
      ['personalityTraits', STAR_METADATA.personalityTraits],
      ['performanceContext', STAR_METADATA.performanceContext],
      ['targetAudience', STAR_METADATA.targetAudience],
    ] as const) {
      const arr = (metadata as any)[key];
      if (arr && Array.isArray(arr)) {
        const msg = validateArrayLength(arr, opts, key);
        if (msg) errors.push({ field: key, message: msg, severity: 'error' });
        else {
          const itemMsg = validateArrayItems(arr, STAR_METADATA.stringMaxLength, key);
          if (itemMsg) errors.push({ field: key, message: itemMsg, severity: 'error' });
        }
      }
    }

    // Business rule: Star name should be meaningful
    if (metadata.starName && metadata.starName.trim().length < 2) {
      warnings.push({
        field: 'starName',
        message: 'Star name should be at least 2 characters long',
        suggestion: 'Provide a more descriptive star name',
      });
    }

    // Business rule: Musical style should be specified for performers
    if (
      metadata.archetype === 'performer' &&
      (!metadata.musicalStyle || metadata.musicalStyle.length === 0)
    ) {
      warnings.push({
        field: 'musicalStyle',
        message: 'Musical style is recommended for performers',
        suggestion: 'Add at least one musical style',
      });
    }

    // Business rule: Vibe should match energy level
    if (metadata.energy === 'high' && metadata.vibe === 'calm') {
      warnings.push({
        field: 'vibe',
        message: 'Vibe may not match energy level',
        suggestion: 'Consider a more energetic vibe for high-energy performers',
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  getDefaultMetadata(): StarsLayerMetadata {
    return {
      layerType: 'stars',
      starName: '',
      gender: 'Male',
      ageGroup: 'adult',
      ethnicity: '',
      bodyType: 'Average',
      height: 'Average',
      hairColor: '',
      hairLength: 'Medium',
      hairStyle: 'Straight',
      hairTexture: 'Medium',
      hairTreatment: 'Natural',
      hairAccessories: 'None',
      eyeColor: '',
      skinTone: 'Medium',
      makeupType: 'Natural',
      eyeMakeup: 'Natural',
      lipMakeup: 'Natural',
      faceMakeup: 'Light Coverage',
      makeupEffects: 'None',
      colorPalette: 'Neutral',
      archetype: 'emerging',
      energy: 'Medium',
      vibe: 'Calm',
      careerStage: 'Rising',
      performanceContext: [],
      personalityTraits: [],
      richDescription: '',
      comprehensiveTags: [],
      variantName: '',
      variantType: '',
      baseAssetId: '',
    };
  }

  getInheritanceRules(): {
    [field: string]: 'always' | 'if_empty' | 'never' | 'merge';
  } {
    return {
      // Always inherit demographic information
      ageGroup: 'always',
      ethnicity: 'always',

      // Inherit if empty for personality traits
      archetype: 'if_empty',
      energy: 'if_empty',
      vibe: 'if_empty',

      // Never inherit physical appearance (variant-specific)
      starName: 'never',
      hairColor: 'never',
      hairStyle: 'never',
      makeupType: 'never',

      // Merge arrays
      musicalStyle: 'merge',
      personalityTraits: 'merge',
    };
  }

  private normalizeArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    return [];
  }
}

// Export singleton instance
export const starsLayerStrategy = new StarsLayerStrategy();
